import { useEffect, useState } from "react";
import { Quote, ShieldCheck } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import api, { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const SPANS = ["lg:col-span-3", "lg:col-span-3", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2"];

export const Testimonials = () => {
  const { t } = useLang();
  const [customTestimonials, setCustomTestimonials] = useState([]);
  const [customPartners, setCustomPartners] = useState([]);

  useEffect(() => {
    api.get("/content/testimonials").then(({ data }) => setCustomTestimonials(data)).catch(() => {});
    api.get("/content/partners").then(({ data }) => setCustomPartners(data)).catch(() => {});
  }, []);

  const testimonials = customTestimonials.length ? customTestimonials : t.testimonials.fallback;
  const partners = customPartners.length ? customPartners : t.testimonials.partnerNames.map((p) => ({ name: p, logo: null }));

  return (
  <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-surface">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <Eyebrow>{t.testimonials.eyebrow}</Eyebrow>
        <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] max-w-xl mb-14">
          {t.testimonials.title}
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5">
        {testimonials.map((tm, i) => (
          <Reveal key={tm.id || i} delay={(i % 3) * 0.07} className={SPANS[i % SPANS.length]}>
            <figure
              data-testid={`testimonial-${i + 1}`}
              className="group h-full bg-midnight border border-[#1E293B] p-8 flex flex-col hover:border-gold/50 hover:-translate-y-1 transition-[border-color,transform] duration-300"
            >
              <Quote className="w-6 h-6 text-gold mb-6 transition-transform duration-300 group-hover:scale-110" />
              <blockquote className="text-base text-[#F4F6F8] leading-relaxed flex-1">
                “{tm.quote}”
              </blockquote>
              <figcaption className="mt-8 pt-5 border-t border-[#1E293B]">
                <p className="text-sm font-semibold text-gold">{tm.name}</p>
                <p className="text-xs text-[#94A3B8] mt-1">{tm.org}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-20">
          <p className="text-xs uppercase tracking-[0.25em] text-[#94A3B8] mb-6 text-center">
            {t.testimonials.partnersLabel}
          </p>
          <div
            data-testid="partner-logos-row"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-[#1E293B] divide-x divide-y lg:divide-y-0 divide-[#1E293B]"
          >
            {partners.map((p) => (
              <div
                key={p.id || p.name}
                data-testid={`partner-logo-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex flex-col items-center justify-center py-8 px-4 group hover:bg-surface2 transition-colors duration-300"
              >
                {p.logo ? (
                  <img
                    src={`${API}/public/files/${p.logo.storage_path}`}
                    alt={p.name}
                    title={p.name}
                    className="h-8 max-w-[140px] object-contain opacity-40 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-[opacity,filter] duration-300"
                  />
                ) : (
                  <span className="font-display font-bold tracking-[0.2em] text-sm text-[#94A3B8]/40 group-hover:text-gold transition-colors duration-300 text-center">
                    {p.name}
                  </span>
                )}
                {p.verified && (
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-gold border border-gold/40 px-2.5 py-1 rounded-full">
                    <ShieldCheck className="w-3.5 h-3.5" /> {t.testimonials.verifiedBadge}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
